import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { setOffline, setOnline } from '../store/reducers/networkSlice';

export const useNetworkStatus = () => {
    const dispatch = useDispatch();
    const isCheckingRef = useRef<boolean>(false);
    const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastCheckTimeRef = useRef<number>(0);
    const CHECK_COOLDOWN_MS = 30000;

    useEffect(() => {
        const checkNetworkStatus = async (skipCooldown = false) => {
            if (isCheckingRef.current) {
                return;
            }

            const now = Date.now();
            if (!skipCooldown && now - lastCheckTimeRef.current < CHECK_COOLDOWN_MS) {
                return;
            }

            isCheckingRef.current = true;
            lastCheckTimeRef.current = now;

            try {
                if (typeof navigator !== 'undefined' && navigator.onLine) {
                    try {
                        const controller = new AbortController();
                        const timeoutId = setTimeout(() => controller.abort(), 5000);

                        await fetch('https://www.cloudflare.com/cdn-cgi/trace', {
                            method: 'GET',
                            cache: 'no-store',
                            signal: controller.signal,
                            mode: 'no-cors',
                        });

                        clearTimeout(timeoutId);
                        dispatch(setOnline());
                    } catch {
                        dispatch(setOffline());
                    }
                }
            } finally {
                isCheckingRef.current = false;
            }
        };

        const debouncedCheck = (skipCooldown = false) => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
            debounceTimeoutRef.current = setTimeout(() => {
                checkNetworkStatus(skipCooldown);
            }, 300);
        };

        const handleOnline = () => {
            // Run immediately when browser reports online again.
            debouncedCheck(true);
        };

        const handleOffline = () => {
            dispatch(setOffline());
        };

        checkNetworkStatus(true);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, [dispatch]);
};
